import { useState } from 'react';
import { useQuery } from 'react-apollo';
import { useSelector } from 'react-redux';

import { PhotoForCommentsAndReactions } from '../../../graphql/queries/photos';
import { MeQuery } from '../../../graphql/queries/account';
import { createLoadMoreMethod } from '../../../util/apollo';
import { VideoForCommentsAndReactions } from '../../../graphql/queries/video';

const useFileReactionList = ({ photoId, videoId, limit = 50 }) => {
  const [loadingMore, setLoadingMore] = useState(false);
  const authenticated = useSelector(({ auth }) => auth.authenticated);
  const { data: { me: user } = {} } = useQuery(MeQuery, {
    skip: !authenticated,
  });
  const {
    data: { photo } = {},
    loading: loadingPhoto,
    error: photoError,
    fetchMore: photoFetchMore,
  } = useQuery(PhotoForCommentsAndReactions, {
    variables: {
      id: photoId,
      root_only: true,
      parentId: undefined,
      account_reactions_limit: limit,
      account_reactions_offset: 0,
      comments_limit: 100,
      comments_offset: 0,
    },
    skip: !photoId,
  });

  const {
    data: { video } = {},
    loading: loadingVideo,
    error: videoError,
    fetchMore: videoFetchMore,
  } = useQuery(VideoForCommentsAndReactions, {
    variables: {
      id: videoId,
      root_only: true,
      parentId: undefined,
      account_reactions_limit: limit,
      account_reactions_offset: 0,
      comments_limit: 100,
      comments_offset: 0,
    },
    skip: !videoId,
  });

  const entity = photo || video;

  const loadMore = createLoadMoreMethod({
    source: entity,
    recordsPath: 'reactions_accounts',
    offsetVariableLabel: 'account_reactions_offset',
    fetchMore: photoFetchMore || videoFetchMore,
    limit: 150,
  });

  const accountReactions = entity?.reactions_accounts?.records ?? [];
  const accountReactionsTotal = entity?.reactions_accounts?.total ?? 0;

  const onLoadMore = async () => {
    if (loadingMore) {
      return;
    }

    setLoadingMore(true);

    try {
      await loadMore();
    } finally {
      setLoadingMore(false);
    }
  };

  return {
    user,
    entity,
    onLoadMore,
    error: photoError || videoError,
    loading: loadingPhoto || loadingVideo,
    accountReactions,
    accountReactionsTotal,
    loadingMore,
    accountFileReaction: entity?.account_file_reaction,
  };
};

export default useFileReactionList;
